import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	createFilterOptions,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useGetAssetAddressesQuery, type Address } from '../../../../api/asset.api';
import { selectAccount } from '../../../../redux/slices/accountSlice';

interface AssetLocationUpdateProps {
	onChange: (data: { effectiveDate: string | null; location: Address | null }) => void;
}

const addressFields = [
	{ name: 'installAddress1', label: 'Address 1' },
	{ name: 'installAddress2', label: 'Address 2' },
	{ name: 'installAddress3', label: 'Address 3' },
	{ name: 'installAddress4', label: 'Address 4' },
	{ name: 'installCity', label: 'City' },
	{ name: 'installState', label: 'State' },
	{ name: 'installPostalCode', label: 'Zip Code' },
	{ name: 'installCounty', label: 'County' },
	{ name: 'installCountry', label: 'Country' },
];

const AssetLocationUpdate: React.FC<AssetLocationUpdateProps> = ({ onChange }) => {
	const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(dayjs());
	const [location, setLocation] = useState<Address | null>(null);
	const account = useSelector(selectAccount);
	const {
		data: addresses = [],
		isLoading,
		isError,
	} = useGetAssetAddressesQuery(
		{
			accountId: account.accountId,
			sourceId: account.sourceId,
			accountTypeCode: account.accountTypeCode,
		},
		{ skip: !account || !account.accountId }
	);
	const [options, setOptions] = useState<Address[]>([]);
	const prevAddressesRef = useRef<Address[]>([]);
	const [showNewLocationForm, setShowNewLocationForm] = useState(false);
	const [newLocationFields, setNewLocationFields] = useState<any>({});

	// Concatenate address fields for display
	const formatAddress = (addr: Address) =>
		[
			addr.installAddress1,
			addr.installAddress2,
			addr.installAddress3,
			addr.installAddress4,
			addr.installCity,
			addr.installState,
			addr.installPostalCode,
			addr.installCounty,
			addr.installCountry,
		]
			.filter(Boolean)
			.join(', ');

	const filterOptions = createFilterOptions({
		stringify: (option) => formatAddress(option as Address),
	});

	// // Sync options with API data (only if changed)
	useEffect(() => {
		// Only update if addresses actually changed
		const addressesChanged = addresses.length !== prevAddressesRef.current.length;

		if (addressesChanged) {
			setOptions(addresses);
			prevAddressesRef.current = addresses;
		}
	}, [addresses]);

	useEffect(() => {
		onChange({
			effectiveDate: effectiveDate ? effectiveDate.format('YYYY-MM-DD') : null,
			location: location ? location : null,
		});
	}, [effectiveDate, location, onChange]);

	// Handlers
	const handleNewLocationButton = () => {
		setShowNewLocationForm(true);
	};

	const handleNewLocationFieldChange = (field: string, value: string) => {
		setNewLocationFields((prev: any) => ({ ...prev, [field]: value }));
	};

	const addLocation = () => {
		const newLocationObj = { ...newLocationFields };
		const newLocationString = formatAddress(newLocationObj);
		if (newLocationString && !options.some((opt) => opt.installAddress1 === newLocationString)) {
			setOptions((prev) => [...prev, newLocationObj]);
			setLocation(newLocationObj);
			setShowNewLocationForm(false);
			setNewLocationFields({});
		}
	};

	const handleChange = (
		_event: React.SyntheticEvent<Element, Event>,
		value: unknown,
		_reason: any,
		_details?: any
	) => {
		setLocation(value as Address | null);
	};

	return (
		<Box data-testid="asset-location-update">
			<DatePicker
				label="Effective Date"
				value={effectiveDate}
				onChange={setEffectiveDate}
				slotProps={{
					textField: {
						fullWidth: true,
						size: 'medium',
						margin: 'normal',
						focused: true,
						inputProps: { 'data-testid': 'asset-location-update-effective-date-picker' },
					},
				}}
				sx={{ width: '50%' }}
			/>
			{isLoading ? (
				<Box display="flex" alignItems="center" mt={2} mb={2}>
					<CircularProgress size={20} />
					<Typography variant="body2" ml={2}>
						Loading locations...
					</Typography>
				</Box>
			) : isError ? (
				<Typography color="error" mt={2} mb={2}>
					Failed to load addresses
				</Typography>
			) : showNewLocationForm ? (
				<Box sx={{ mt: 2, mb: 2 }}>
					<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
						{addressFields.map((field) => (
							<TextField
								key={field.name}
								label={field.label}
								value={newLocationFields[field.name] || ''}
								onChange={(e) => handleNewLocationFieldChange(field.name, e.target.value)}
								fullWidth
								margin="dense"
								slotProps={{ htmlInput: { 'data-testid': `new-location-${field.name}-input` } }}
							/>
						))}
					</Box>
					<Stack direction="row" spacing={2} mt={2}>
						<Button variant="contained" size="small" onClick={addLocation}>
							Add Location
						</Button>
						<Button
							type="button"
							variant="text"
							size="small"
							onClick={() => setShowNewLocationForm(false)}
						>
							Cancel
						</Button>
					</Stack>
				</Box>
			) : (
				<Autocomplete
					options={options}
					getOptionLabel={(opt) => formatAddress(opt as Address) || ''}
					value={location}
					onChange={handleChange}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Install at Location"
							margin="normal"
							fullWidth
							focused
							value={location}
							data-testid="asset-location-autocomplete-input"
						/>
					)}
					isOptionEqualToValue={(opt, val) =>
						formatAddress(opt as Address) === formatAddress(val as Address)
					}
					freeSolo={false}
					filterOptions={filterOptions}
					sx={{ width: '50%' }}
				/>
			)}
			<Typography variant="caption" color="text.secondary" mt={1}>
				If you don't see your location in the list, click on the "New Location" button to add it.
			</Typography>
			<Stack direction="row" justifyContent="flex-start" mt={1}>
				<Button
					size="large"
					variant="text"
					onClick={handleNewLocationButton}
					sx={{
						textTransform: 'none',
						p: 0,
						minWidth: 0,
						color: (theme) => theme.palette.brand.primary,
					}}
					data-testid="asset-location-new-location-button"
				>
					New Location &gt;
				</Button>
			</Stack>
		</Box>
	);
};

export default AssetLocationUpdate;
